import {
  Banner as BannerRoot,
  BannerBody,
  BannerContent,
  BannerCta,
  BannerDescription,
  BannerEyebrow,
  BannerImage,
  BannerLink,
  BannerTitle,
  BannerVariant,
  bannerHasCta,
  type BannerVariant as BannerVariantT,
} from '@vs-shop/commerce-ui/banner';

import { getBannerMediaProps } from '../../../utils/getBannerMediaProps';

import type { Banner as BannerT } from '../../../effect/schema/openapi/backend';

type Props = {
  banner: BannerT;
  /**
   * Required: the appearance is the placement's decision, and a default here
   * would let a call site omit it and silently get the full-width banner in a
   * product panel.
   */
  variant: BannerVariantT;
};

/**
 * rcprofi's banner: the shared `@vs-shop/commerce-ui` section, arranged once and
 * fed this tenant's data.
 *
 * What stays here is what cannot live in a shared package — the CMS row shape,
 * the Sylius/Liip media pipeline behind `getBannerMediaProps`, and the
 * per-instance CMS colours, which reach the parts as final CSS values on the
 * native `style` prop. The appearance belongs to the parts.
 *
 * The click affordance is `BannerLink`, an overlay anchor covering the section.
 * Beside a CTA it is hidden from assistive tech and dropped from the tab order,
 * leaving the CTA as the one focusable link; alone, it carries the accessible
 * name. Either way the whole surface is clickable.
 */
export default function Banner({ banner, variant }: Props) {
  const link = banner.link ?? null;
  const title = banner.title ?? null;
  const buttonText = banner.buttonText ?? null;
  const hasOwnCta = bannerHasCta({ variant, ctaText: buttonText });

  return (
    <BannerRoot variant={variant}>
      <BannerImage {...getBannerMediaProps(banner, variant)} alt={title ?? ''} />

      <BannerContent variant={variant}>
        <BannerBody
          variant={variant}
          {...(banner.highlightEnabled && banner.highlightColor
            ? { highlightColor: banner.highlightColor }
            : {})}
        >
          {banner.label ? (
            <BannerEyebrow variant={variant} style={{ color: banner.labelColor }}>
              {banner.label}
            </BannerEyebrow>
          ) : null}

          {title ? (
            <BannerTitle variant={variant} style={{ color: banner.titleColor }}>
              {title}
            </BannerTitle>
          ) : null}

          {banner.description ? (
            <BannerDescription variant={variant} style={{ color: banner.descriptionColor }}>
              {banner.description}
            </BannerDescription>
          ) : null}
        </BannerBody>

        <BannerCta
          variant={variant}
          {...(link === null ? {} : { href: link })}
          style={{ color: banner.buttonTextColor, borderColor: banner.buttonTextColor }}
        >
          {buttonText}
        </BannerCta>
      </BannerContent>

      {/* An overlay anchor with no destination is inert but still covers the
          copy, blocking selection — so with no link there is no overlay. */}
      {link === null ? null : (
        <BannerLink
          href={link}
          {...(hasOwnCta ? { 'aria-hidden': true } : {})}
          {...(hasOwnCta || title === null ? {} : { 'aria-label': title })}
        />
      )}
    </BannerRoot>
  );
}
